import React from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";

export const RenewalDialog = ({
  open, onOpenChange,
  selectedEnrollment,
  tenures,
  pricing = [],
  batches = [],
  renewalForm,
  renewalPreview, renewalPreviewLoading,
  onTenureChange,
  onStartDateChange,
  onChargeTypeChange,
  onDiscountTypeChange,
  onDiscountValueChange,
  onRenewalModeChange,
  onSessionCountChange,
  isMilestoneMode = false,
  paymentForm, setPaymentForm,
  onSubmit,
  onMarkNoRenewal,
}) => {
  // Filter tenures by pricing for the enrollment's batch style
  const enrolledBatch = batches.find((b) => b.batch_id === selectedEnrollment?.batch_id);
  const styleId = enrolledBatch?.style_id || selectedEnrollment?.batch?.style_id;
  const pricedTenures = React.useMemo(() => {
    if (!styleId) return tenures;
    const tenureIds = new Set(
      pricing.filter((p) => p.style_id === styleId).map((p) => p.tenure_id)
    );
    return tenures.filter((t) => tenureIds.has(t.tenure_id));
  }, [styleId, pricing, tenures]);
  const priceFor = React.useCallback(
    (tenureId) => pricing.find((p) => p.style_id === styleId && p.tenure_id === tenureId),
    [pricing, styleId]
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Renew Subscription</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">Batch:</p>
            <p className="font-medium">{selectedEnrollment?.batch?.name}</p>
            {selectedEnrollment?.next_renewal_date && (
              <p className="text-xs text-slate-500 mt-1">
                Previous renewal date: {format(new Date(selectedEnrollment.next_renewal_date), "dd MMM yyyy")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Select Tenure</Label>
            <select
              data-testid="renewal-tenure-select"
              value={renewalForm.tenure_id}
              onChange={(e) => onTenureChange(e.target.value)}
              className="w-full h-12 rounded-xl border px-4 bg-white"
              disabled={isMilestoneMode && renewalForm.renewal_mode !== "tenure"}
            >
              <option value="">
                {pricedTenures.length === 0 ? "No priced tenures for this style" : "Choose Tenure"}
              </option>
              {pricedTenures.map((tenure) => {
                const p = priceFor(tenure.tenure_id);
                const amt = p && p.price !== undefined ? `₹${Number(p.price).toLocaleString("en-IN")}` : "";
                return (
                  <option key={tenure.tenure_id} value={tenure.tenure_id}>
                    {tenure.name} ({tenure.duration_months} months){amt ? ` — ${amt}` : ""}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Feb 2026 — Milestone renewal mode toggle + session count input.
              Only visible when the parent opts into milestone mode. */}
          {isMilestoneMode && (
            <>
              <div className="space-y-2" data-testid="renewal-mode-section">
                <Label className="text-xs font-semibold">Renewal Mode</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => onRenewalModeChange?.("tenure")}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${renewalForm.renewal_mode === "tenure" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}
                    data-testid="renewal-mode-tenure-btn"
                  >
                    Tenure-based
                  </button>
                  <button
                    onClick={() => onRenewalModeChange?.("session")}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${renewalForm.renewal_mode === "session" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}
                    data-testid="renewal-mode-session-btn"
                  >
                    Session-based
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Tenure = milestone-anchored cycles.  Session = pay for exactly N classes.
                </p>
              </div>

              {renewalForm.renewal_mode === "session" && (
                <div className="space-y-2" data-testid="renewal-session-count-section">
                  <Label>Number of Sessions</Label>
                  <Input
                    data-testid="renewal-session-count-input"
                    type="number"
                    min="1"
                    value={renewalForm.session_count || ""}
                    onChange={(e) => onSessionCountChange?.(e.target.value)}
                    className="h-12 rounded-xl"
                    placeholder="e.g. 6"
                  />
                  <p className="text-xs text-slate-500">
                    Renewal date = date of the Nth scheduled class from the start date.
                  </p>
                </div>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label>Renewal Start Date</Label>
            <Input
              data-testid="renewal-start-date"
              type="date"
              value={renewalForm.start_date}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="h-12 rounded-xl"
            />
            <p className="text-xs text-slate-500">
              {isMilestoneMode
                ? "Defaults to the day after the previous enrollment ended. Adjust for genuine late-return cases."
                : "Renewals should ideally start on the 1st of the month"}
            </p>
          </div>

          {renewalForm.start_date && (!isMilestoneMode || renewalForm.renewal_mode === "tenure") && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Pricing Mode</Label>
              <div className="flex gap-2">
                <button
                  onClick={() => onChargeTypeChange?.("full")}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${renewalForm.charge_type === "full" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}
                  data-testid="renewal-charge-type-full"
                >
                  Full Fee
                </button>
                <button
                  onClick={() => onChargeTypeChange?.("prorated")}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${renewalForm.charge_type === "prorated" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}
                  data-testid="renewal-charge-type-prorated"
                  disabled={isMilestoneMode && renewalPreview && !renewalPreview.is_prorated_available}
                  title={isMilestoneMode && renewalPreview && !renewalPreview.is_prorated_available ? "Prorated not applicable — start date lands on a milestone boundary" : ""}
                >
                  Prorated
                </button>
              </div>
              {isMilestoneMode && renewalPreview && !renewalPreview.is_prorated_available && (
                <p className="text-xs text-slate-500">Start date lands exactly on a milestone — no proration needed.</p>
              )}
            </div>
          )}

          {renewalForm.start_date && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Discount (optional)</Label>
              <div className="flex gap-2">
                <select
                  data-testid="renewal-discount-type-select"
                  value={renewalForm.discount_type || ""}
                  onChange={(e) => onDiscountTypeChange?.(e.target.value)}
                  className="h-10 rounded-xl border px-3 bg-white text-sm flex-1"
                >
                  <option value="">No Discount</option>
                  <option value="flat">Fixed Amount (₹)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
                {renewalForm.discount_type && (
                  <Input
                    data-testid="renewal-discount-value-input"
                    type="number"
                    min="0"
                    max={renewalForm.discount_type === "percentage" ? "100" : undefined}
                    placeholder={renewalForm.discount_type === "percentage" ? "%" : "₹"}
                    value={renewalForm.discount_value || ""}
                    onChange={(e) => onDiscountValueChange?.(e.target.value)}
                    className="h-10 rounded-xl text-sm w-24"
                  />
                )}
              </div>
            </div>
          )}

          {renewalPreviewLoading && (
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500">Calculating...</p>
            </div>
          )}

          {renewalPreview && !renewalPreviewLoading && !isMilestoneMode && (
            <div data-testid="renewal-proration-preview" className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Classes</p>
                  <p className="text-lg font-bold text-slate-900">{renewalPreview.total_classes}</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Per Class</p>
                  <p className="text-lg font-bold text-slate-900">₹{renewalPreview.price_per_class}</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Renewal</p>
                  <p className="text-sm font-medium text-slate-700">
                    {renewalPreview.next_renewal_date
                      ? format(new Date(renewalPreview.next_renewal_date), "dd MMM yyyy")
                      : "—"}
                  </p>
                </div>
              </div>
              {renewalPreview.tenure_end_capped && (
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-200" data-testid="renewal-tenure-cap-notice">
                  <p className="text-xs text-blue-800">
                    <strong>Tenure capped at batch end.</strong> The batch ends on{" "}
                    {renewalPreview.batch_end_date ? format(new Date(renewalPreview.batch_end_date), "dd MMM yyyy") : "the batch end date"}.
                    The renewal ends with the batch and the fee has been adjusted accordingly.
                  </p>
                </div>
              )}
              <div className="space-y-1">
                {Object.entries(renewalPreview.monthly_breakdown).map(([month, count]) => (
                  <div key={month} className="flex justify-between text-sm">
                    <span className="text-slate-600">{month}</span>
                    <span className="font-medium">{count} classes</span>
                  </div>
                ))}
              </div>

              <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-100">
                <div className="flex items-center justify-between p-2.5 text-sm">
                  <span className="text-slate-500">Original Fee (Full Tenure)</span>
                  <span className={`font-medium ${renewalForm.charge_type === "full" ? "text-slate-900" : "text-slate-400 line-through"}`}>
                    ₹{renewalPreview.original_fee ?? renewalPreview.rate_card_price}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2.5 text-sm">
                  <span className="text-slate-500">Prorated Fee ({renewalPreview.total_classes} classes)</span>
                  <span className={`font-medium ${renewalForm.charge_type === "prorated" ? "text-slate-900" : "text-slate-400 line-through"}`}>
                    ₹{renewalPreview.prorated_fee ?? renewalPreview.enrollment_fee}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2.5 bg-slate-50 text-sm">
                  <span className="text-slate-700 font-medium">Base Fee ({renewalForm.charge_type === "full" ? "Full" : "Prorated"})</span>
                  <span className="font-semibold text-slate-900">₹{renewalPreview.base_fee ?? renewalPreview.enrollment_fee}</span>
                </div>
                {(renewalPreview.discount_amount ?? 0) > 0 && (
                  <div className="flex items-center justify-between p-2.5 text-sm bg-green-50">
                    <span className="text-green-700">
                      Discount ({renewalPreview.discount_type === "percentage" ? `${renewalPreview.discount_value}%` : `₹${renewalPreview.discount_value}`})
                    </span>
                    <span className="font-semibold text-green-700">−₹{renewalPreview.discount_amount}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between border-t pt-2">
                <p className="text-sm text-slate-600">Final Payable</p>
                <p data-testid="renewal-final-fee" className="text-2xl font-bold text-emerald-700">
                  ₹{renewalPreview.final_fee ?? renewalPreview.enrollment_fee}
                </p>
              </div>
            </div>
          )}

          {/* Milestone-mode preview panel (Feb 2026) */}
          {renewalPreview && !renewalPreviewLoading && isMilestoneMode && renewalPreview.status === "success" && (
            <div data-testid="renewal-milestone-preview" className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Classes</p>
                  <p className="text-lg font-bold text-slate-900">
                    {renewalPreview.classes_delivered}
                    {renewalPreview.renewal_mode === "tenure" && renewalPreview.nominal_class_count !== renewalPreview.classes_delivered && (
                      <span className="text-xs text-slate-500"> / {renewalPreview.nominal_class_count}</span>
                    )}
                  </p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">
                    {renewalPreview.renewal_mode === "session" ? "Per Session" : "Renewal Date"}
                  </p>
                  {renewalPreview.renewal_mode === "session" ? (
                    <p className="text-lg font-bold text-slate-900">₹{renewalPreview.per_session_rate}</p>
                  ) : (
                    <p className="text-sm font-medium text-slate-700">
                      {format(new Date(renewalPreview.renewal_date), "dd MMM yyyy")}
                    </p>
                  )}
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">
                    {renewalPreview.renewal_mode === "session" ? "Ends On" : "Mode"}
                  </p>
                  {renewalPreview.renewal_mode === "session" ? (
                    <p className="text-sm font-medium text-slate-700">
                      {format(new Date(renewalPreview.renewal_date), "dd MMM yyyy")}
                    </p>
                  ) : (
                    <p className="text-sm font-medium text-slate-700">{renewalPreview.tenure_months}-month</p>
                  )}
                </div>
              </div>

              {renewalPreview.capped_at_batch_end && renewalPreview.capped_session_count && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="renewal-session-cap-warning">
                  <p className="text-sm text-amber-900">
                    <strong>Capped at {renewalPreview.capped_session_count} sessions.</strong> Only {renewalPreview.capped_session_count} scheduled classes remain before the batch ends. Extend the batch's end date if you need the full session count.
                  </p>
                </div>
              )}

              {renewalPreview.renewal_mode === "tenure" && (
                <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-100">
                  <div className="flex items-center justify-between p-2.5 text-sm">
                    <span className="text-slate-500">Full Tenure Fee</span>
                    <span className={`font-medium ${renewalForm.charge_type === "full" ? "text-slate-900" : "text-slate-400 line-through"}`}>
                      ₹{renewalPreview.full_fee}
                    </span>
                  </div>
                  {renewalPreview.is_prorated_available && (
                    <div className="flex items-center justify-between p-2.5 text-sm">
                      <span className="text-slate-500">
                        Prorated Fee ({renewalPreview.classes_delivered} of {renewalPreview.nominal_class_count} classes)
                      </span>
                      <span className={`font-medium ${renewalForm.charge_type === "prorated" ? "text-slate-900" : "text-slate-400 line-through"}`}>
                        ₹{renewalPreview.prorated_fee}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {renewalPreview.renewal_mode === "session" && (
                <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-sm flex items-center justify-between">
                  <span className="text-slate-500">
                    {renewalPreview.classes_delivered} sessions × ₹{renewalPreview.per_session_rate}
                  </span>
                  <span className="font-medium text-slate-900">
                    ₹{renewalPreview.original_fee}
                  </span>
                </div>
              )}

              {renewalPreview.discount_amount > 0 && (
                <div className="flex items-center justify-between p-2.5 text-sm bg-green-50 rounded-xl border border-green-100">
                  <span className="text-green-700">Discount</span>
                  <span className="font-semibold text-green-700">
                    −₹{renewalPreview.renewal_mode === "session"
                      ? renewalPreview.discount_amount
                      : (renewalForm.charge_type === "prorated" ? renewalPreview.prorated_discount : renewalPreview.full_discount)}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between border-t pt-2">
                <p className="text-sm text-slate-600">Final Payable</p>
                <p data-testid="renewal-final-fee" className="text-2xl font-bold text-emerald-700">
                  ₹{renewalPreview.renewal_mode === "session"
                    ? renewalPreview.session_fee
                    : (renewalForm.charge_type === "prorated" ? renewalPreview.prorated_fee : renewalPreview.full_fee)}
                </p>
              </div>
            </div>
          )}

          {/* Milestone-mode error banner */}
          {renewalPreview && !renewalPreviewLoading && isMilestoneMode && renewalPreview.status === "error" && (
            <div data-testid="renewal-milestone-error" className="p-3 bg-red-50 rounded-xl border border-red-200">
              <p className="text-sm text-red-800">
                <strong>Cannot resolve renewal:</strong> {renewalPreview.message}
              </p>
            </div>
          )}

          {renewalPreview && (
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-900 mb-3">Payment Details</h4>

            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="renewal_payment_status"
                    checked={paymentForm.payment_status === "completed"}
                    onChange={() => setPaymentForm({...paymentForm, payment_status: "completed", amount_paid: ""})}
                  />
                  <span>Full Payment</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="renewal_payment_status"
                    checked={paymentForm.payment_status === "partial"}
                    onChange={() => setPaymentForm({...paymentForm, payment_status: "partial"})}
                  />
                  <span>Partial</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="renewal_payment_status"
                    checked={paymentForm.payment_status === "pending"}
                    onChange={() => setPaymentForm({...paymentForm, payment_status: "pending", amount_paid: ""})}
                  />
                  <span>Pending</span>
                </label>
              </div>

              {paymentForm.payment_status === "partial" && renewalPreview && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                  <div className="space-y-2">
                    <Label>Amount Paid (₹)</Label>
                    <Input
                      type="number"
                      value={paymentForm.amount_paid}
                      onChange={(e) => setPaymentForm({...paymentForm, amount_paid: e.target.value})}
                      placeholder="Enter amount"
                      max={renewalPreview.final_fee ?? renewalPreview.total_payable ?? renewalPreview.enrollment_fee}
                      className="h-10 rounded-lg"
                    />
                  </div>
                  {paymentForm.amount_paid && (
                    <p className="text-sm text-amber-700">
                      Balance: ₹{Math.max(0, (renewalPreview.final_fee ?? renewalPreview.total_payable ?? renewalPreview.enrollment_fee) - parseFloat(paymentForm.amount_paid || 0))} will be pending
                    </p>
                  )}
                </div>
              )}

              {(paymentForm.payment_status === "completed" || paymentForm.payment_status === "partial") && (
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                    className="w-full h-10 rounded-lg border px-3 bg-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              )}

              {paymentForm.payment_method === "cheque" && paymentForm.payment_status !== "pending" && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Input
                      value={paymentForm.cheque_bank_name}
                      onChange={(e) => setPaymentForm({...paymentForm, cheque_bank_name: e.target.value})}
                      placeholder="Enter bank name"
                      className="h-10 rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cheque Number</Label>
                    <Input
                      value={paymentForm.cheque_number}
                      onChange={(e) => setPaymentForm({...paymentForm, cheque_number: e.target.value})}
                      placeholder="Enter cheque number"
                      className="h-10 rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          <Button
            data-testid="renewal-submit-btn"
            onClick={onSubmit}
            disabled={
              !renewalForm.start_date ||
              !renewalPreview ||
              (isMilestoneMode && renewalPreview.status === "error") ||
              (isMilestoneMode && renewalForm.renewal_mode === "tenure" && !renewalForm.tenure_id) ||
              (isMilestoneMode && renewalForm.renewal_mode === "session" && !(renewalForm.session_count && parseInt(renewalForm.session_count) > 0)) ||
              (!isMilestoneMode && !renewalForm.tenure_id)
            }
            className="w-full h-12 rounded-full bg-green-500 hover:bg-green-600"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Renew Subscription
          </Button>

          {onMarkNoRenewal && selectedEnrollment && !selectedEnrollment.renewal_exempt && (
            <Button
              data-testid="renewal-mark-no-renewal-btn"
              variant="outline"
              onClick={() => onMarkNoRenewal(selectedEnrollment)}
              className="w-full h-11 rounded-full text-slate-700 border-slate-300 hover:bg-slate-100"
              title="Mark this enrollment as completed; suppress renewal tracking."
            >
              Mark as No Renewal Required
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RenewalDialog;
